import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const FIELD_OPTIONS = ['nric', 'address', 'bankAccount', 'emergencyContact', 'education', 'workExperience'];

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function Onboarding() {
  const [mandatoryFields, setMandatoryFields] = useState<string[]>([]);
  const [checklistType, setChecklistType] = useState<'onboarding' | 'offboarding'>('onboarding');
  const [checklist, setChecklist] = useState<any[]>([]);
  const [newItem, setNewItem] = useState('');
  const [assets, setAssets] = useState<any[]>([]);
  const [newAsset, setNewAsset] = useState({ assetName: '', assetType: '', quantity: '1' });

  const fetchAll = async (type: string) => {
    const [setup, items, assetList] = await Promise.all([
      api.get('/hr/onboarding/initial-setup'),
      api.get('/hr/onboarding/checklists', { params: { type } }),
      api.get('/hr/onboarding/default-assets'),
    ]);
    setMandatoryFields(setup.data.mandatoryFields || []);
    setChecklist(items.data.items || []);
    setAssets(assetList.data.assets || []);
  };

  useEffect(() => {
    fetchAll(checklistType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistType]);

  const toggleField = (field: string) => {
    setMandatoryFields((prev) => (prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]));
  };

  const saveSetup = async () => {
    await api.put('/hr/onboarding/initial-setup', { mandatoryFields });
    alert('Initial setup saved');
  };

  const addChecklistItem = async () => {
    if (!newItem) return;
    await api.post('/hr/onboarding/checklists', { checklistType, itemName: newItem, itemOrder: checklist.length });
    setNewItem('');
    fetchAll(checklistType);
  };

  const removeChecklistItem = async (id: string) => {
    await api.delete(`/hr/onboarding/checklists/${id}`);
    fetchAll(checklistType);
  };

  const addAsset = async () => {
    if (!newAsset.assetName) return;
    await api.post('/hr/onboarding/default-assets', { ...newAsset, quantity: Number(newAsset.quantity) });
    setNewAsset({ assetName: '', assetType: '', quantity: '1' });
    fetchAll(checklistType);
  };

  const removeAsset = async (id: string) => {
    await api.delete(`/hr/onboarding/default-assets/${id}`);
    fetchAll(checklistType);
  };

  const [offboardEmployeeId, setOffboardEmployeeId] = useState('');
  const [offboardForm, setOffboardForm] = useState({ lastWorkingDay: '', resignationReason: '' });

  const initiateOffboarding = async () => {
    if (!offboardEmployeeId || !offboardForm.lastWorkingDay) return alert('Employee and last working day are required');
    try {
      await api.post(`/hr/employees/${offboardEmployeeId}/offboard`, offboardForm);
      alert('Offboarding initiated — checklist seeded');
      setOffboardEmployeeId('');
      setOffboardForm({ lastWorkingDay: '', resignationReason: '' });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to initiate offboarding');
    }
  };

  const completeOffboarding = async () => {
    if (!offboardEmployeeId) return alert('Select an employee first');
    if (!window.confirm('Deactivate this employee? This completes offboarding.')) return;
    await api.put(`/hr/employees/${offboardEmployeeId}/offboard/complete`);
    alert('Employee offboarded');
  };

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold">Onboarding Setup</h1>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="font-semibold mb-3">Initial Setup — Mandatory Candidate Portal Fields</h2>
        <div className="flex flex-wrap gap-4 mb-4">
          {FIELD_OPTIONS.map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={mandatoryFields.includes(f)} onChange={() => toggleField(f)} />
              {f}
            </label>
          ))}
        </div>
        <Button onClick={saveSetup}>Save</Button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Checklist</h2>
          <Select
            value={checklistType}
            onChange={(e) => setChecklistType(e.target.value as any)}
            options={[
              { value: 'onboarding', label: 'Onboarding Checklist' },
              { value: 'offboarding', label: 'Offboarding Checklist' },
            ]}
          />
        </div>
        <ul className="space-y-2 mb-4">
          {checklist.map((item) => (
            <li key={item.id} className="flex justify-between items-center text-sm border-b py-2">
              {item.item_name}
              <button className="text-red-600 hover:underline" onClick={() => removeChecklistItem(item.id)}>
                Remove
              </button>
            </li>
          ))}
          {checklist.length === 0 && <li className="text-gray-500 text-sm">No items yet</li>}
        </ul>
        <div className="flex gap-2">
          <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="New checklist item" className="flex-1" />
          <Button onClick={addChecklistItem}>Add</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="font-semibold mb-3">Default Assets for New Employees</h2>
        <ul className="space-y-2 mb-4">
          {assets.map((a) => (
            <li key={a.id} className="flex justify-between items-center text-sm border-b py-2">
              {a.asset_name} ({a.asset_type}) x{a.quantity}
              <button className="text-red-600 hover:underline" onClick={() => removeAsset(a.id)}>
                Remove
              </button>
            </li>
          ))}
          {assets.length === 0 && <li className="text-gray-500 text-sm">No default assets configured</li>}
        </ul>
        <div className="grid grid-cols-4 gap-2">
          <Input placeholder="Asset name" value={newAsset.assetName} onChange={(e) => setNewAsset({ ...newAsset, assetName: e.target.value })} />
          <Input placeholder="Type" value={newAsset.assetType} onChange={(e) => setNewAsset({ ...newAsset, assetType: e.target.value })} />
          <Input placeholder="Qty" type="number" value={newAsset.quantity} onChange={(e) => setNewAsset({ ...newAsset, quantity: e.target.value })} />
          <Button onClick={addAsset}>Add</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="font-semibold mb-3">Offboarding</h2>
        <div className="grid grid-cols-3 gap-4 items-end">
          <LookupSelect label="Employee" value={offboardEmployeeId} onChange={(v) => setOffboardEmployeeId(v)} fetchOptions={fetchEmployeeOptions} />
          <Input label="Last Working Day" type="date" value={offboardForm.lastWorkingDay} onChange={(e) => setOffboardForm({ ...offboardForm, lastWorkingDay: e.target.value })} />
          <Input label="Reason" value={offboardForm.resignationReason} onChange={(e) => setOffboardForm({ ...offboardForm, resignationReason: e.target.value })} />
        </div>
        <div className="flex gap-3 mt-4">
          <Button onClick={initiateOffboarding}>Initiate Offboarding</Button>
          <Button variant="danger" onClick={completeOffboarding}>
            Complete Offboarding (Deactivate)
          </Button>
        </div>
      </div>
    </div>
  );
}
